import type { VoidFunctionComponent } from 'react';

import { CodeViewer } from '../../../../generic/CodeViewer/CodeViewer';
import CliLicenseScannedMatches from '../../../../licenses/CliLicenseScannedMatches';
import FileContentsLoading from '../FileContentsLoading';
import useRevisionFileBlobForCodemirror from '../hooks/useRevisionFileBlobForCodemirror';
import type { RevisionFileMatch } from '../../../../../hooks/api/revisions/useFileMatchesForRevision';

import FileContentsNotFound from './FileContentsNotFound';
import { FileViewerFileContentsTooLarge } from './FileContentsTooLarge';
import { parseHighlightSources } from './utils';

import styles from '../FileViewer.module.less';

/**
 * Trying to display files that are too large will crash the browser tab.
 * Instead, limit the size of files that can be shown. In their place,
 * show a simple "file is too large to show" message. We know that 4MB
 * is too large, so we'll keep the size limit at 2MB (to be safe).
 */
export const FILE_VIEWER_SIZE_LIMIT = 2 * 1024 * 1024;

type FileViewerFileContentsProps = {
  revisionLocator: string;
  filePath: string;
  fileContents: string;
  sources: RevisionFileMatch['sources'];
  className?: string;
  isLoading: boolean;
  isNotFound: boolean;
  isError: boolean;
  error: string;
  isCliLicenseScanWithMatchData: boolean;
};

/**
 * Renders the file view contents, without fetching the actual revisionLocator at all.
 */
export const FileViewerFileContents: VoidFunctionComponent<FileViewerFileContentsProps> = ({
  revisionLocator,
  filePath,
  fileContents,
  sources,
  className,
  isLoading,
  isNotFound,
  isError,
  error,
  isCliLicenseScanWithMatchData,
}) => {
  if (isLoading) {
    return <FileContentsLoading />;
  }

  //  Not found errors are handled separately,
  //  as we display a "reanalyze dependency" button.
  if (isNotFound) {
    return (
      <FileContentsNotFound revisionLocator={revisionLocator} />
    );
  }

  //  Display any other errors.
  if (isError) {
    return (
      <div className={styles.error}>
        {error?.toString() || error}
      </div>
    );
  }

  if (isCliLicenseScanWithMatchData && fileContents) {
    return (
      <div className={className}>
        <CliLicenseScannedMatches
          licenseMatchJson={fileContents}
        />
      </div>
    );
  }

  const fileSize = fileContents?.length ?? 0;
  if (fileSize > FILE_VIEWER_SIZE_LIMIT) {
    return (
      <FileViewerFileContentsTooLarge
        revisionLocator={revisionLocator}
        filePath={filePath}
      />

    );
  }

  //  This is how we highlight the sections of text in the file
  //  which were discovered to be matches.
  const highlights = parseHighlightSources(sources).map(({ from, to }) => ({
    from,
    to,
    className: styles.matchedText,
    lineClassName: styles.highlightedLine,
  }));

  return (
    <CodeViewer
      className={className}
      fileContents={fileContents}
      fileName={filePath}
      highlights={highlights}
      scrollToFirstHighlight
    />
  );
};

type Props = {
  //  Locator for revision, including the version.
  revisionLocator: string;
  //  File path within the revision.
  filePath: string;
  //  Where the matches were found in the file, if anywhere.
  sources: RevisionFileMatch['sources'];
  // Check if the license data is from cli scan.
  isCliLicenseScanWithMatchData: boolean;
  className?: string;
};

/**
 * FileViewerFileContents is a wrapper around Codemirror.
 *
 * Fetches a file blob for a given revision + file path,
 * then displays it in a read-only Codemirror container.
 */
const FileViewerFileContentsContainer: VoidFunctionComponent<Props> = ({
  revisionLocator,
  filePath,
  sources,
  isCliLicenseScanWithMatchData,
  className,
}) => {
  const {
    error,
    isError,
    isLoading,
    fileContents,
    isNotFound,
  } = useRevisionFileBlobForCodemirror(revisionLocator, filePath);

  return (
    <FileViewerFileContents
      revisionLocator={revisionLocator}
      filePath={filePath}
      fileContents={fileContents || ''}
      sources={sources}
      isLoading={isLoading}
      isNotFound={isNotFound}
      isError={isError}
      error={error}
      className={className}
      isCliLicenseScanWithMatchData={isCliLicenseScanWithMatchData}
    />
  );
};

export default FileViewerFileContentsContainer;
